import { ApiError, NetworkError, type ErrorEnvelope } from './errors'
import {
  isOfflineResponse,
  reportNetworkFailure,
  reportNetworkSuccess,
} from '../../features/pwa/useOnline'
import {
  clearSession,
  readSession,
  writeSession,
  type StoredSession,
} from './session-storage'

/**
 * Caminho relativo por padrão: a API precisa ser same-origin com o app para
 * que o manifest do PWA seja aceito pelo navegador. Ver docs/specs/11-pwa.md.
 */
export const API_URL = import.meta.env.VITE_API_URL ?? '/api'

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  /** multipart — o browser define o boundary sozinho. */
  formData?: FormData
  signal?: AbortSignal
  /** Pula o header Authorization (rotas públicas). */
  anonymous?: boolean
  query?: Record<string, string | number | boolean | undefined | null>
}

/**
 * Escopo da sessão em uso. Trocado pelo boot da aplicação: `undefined` no
 * painel, o slug do personal no portal.
 */
let activeSlug: string | undefined

export function setActiveSlug(slug: string | undefined): void {
  activeSlug = slug
}

export function getActiveSlug(): string | undefined {
  return activeSlug
}

type SessionListener = (session: StoredSession | null) => void
const listeners = new Set<SessionListener>()

export function onSessionChange(listener: SessionListener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function emit(session: StoredSession | null): void {
  for (const listener of listeners) listener(session)
}

export function storeSession(session: StoredSession): void {
  writeSession(session, activeSlug)
  emit(session)
}

export function dropSession(): void {
  clearSession(activeSlug)
  emit(null)
}

// ── Refresh com fila ──────────────────────────────────────────

/**
 * Uma renovação por vez. Requisições concorrentes que recebem 401 esperam
 * a mesma promise — sem isso, cinco chamadas paralelas disparariam cinco
 * refreshes, e a rotação revogaria a família inteira por "reuso".
 */
let refreshInFlight: Promise<string | null> | null = null

async function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight

  refreshInFlight = (async () => {
    const current = readSession(activeSlug)
    if (!current?.refreshToken) return null

    try {
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: current.refreshToken }),
      })

      if (!response.ok) {
        dropSession()
        return null
      }

      const session = (await response.json()) as StoredSession
      storeSession(session)
      return session.accessToken
    } catch {
      return null
    } finally {
      refreshInFlight = null
    }
  })()

  return refreshInFlight
}

// ── Requisição ────────────────────────────────────────────────

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const base = `${API_URL}${path.startsWith('/') ? path : `/${path}`}`
  const url = new URL(base, window.location.origin)

  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value))
      }
    }
  }

  return url.toString()
}

async function execute(path: string, options: RequestOptions, token?: string): Promise<Response> {
  const headers: Record<string, string> = {}

  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  if (token) headers.Authorization = `Bearer ${token}`

  try {
    const response = await fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers,
      body: options.formData ?? (options.body !== undefined ? JSON.stringify(options.body) : undefined),
      signal: options.signal,
    })

    // O estado de rede vem da REQUISIÇÃO, não de navigator.onLine, que
    // reporta conectado num Wi-Fi sem saída. Ver features/pwa/useOnline.ts.
    if (isOfflineResponse(response)) reportNetworkFailure()
    else reportNetworkSuccess()

    return response
  } catch {
    reportNetworkFailure()
    throw new NetworkError()
  }
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const session = options.anonymous ? null : readSession(activeSlug)

  let response = await execute(path, options, session?.accessToken)

  // Renova uma única vez por requisição e refaz a chamada.
  if (response.status === 401 && !options.anonymous && session) {
    const token = await refreshAccessToken()
    if (!token) {
      dropSession()
      throw new ApiError(await toEnvelope(response, path))
    }
    response = await execute(path, options, token)
  }

  if (response.status === 204) {
    return undefined as T
  }

  if (!response.ok) {
    throw new ApiError(await toEnvelope(response, path))
  }

  const contentType = response.headers.get('content-type') ?? ''
  if (!contentType.includes('application/json')) {
    return (await response.text()) as T
  }

  return (await response.json()) as T
}

async function toEnvelope(response: Response, path: string): Promise<ErrorEnvelope> {
  try {
    const body = (await response.json()) as Partial<ErrorEnvelope>
    return {
      statusCode: body.statusCode ?? response.status,
      error: body.error ?? 'Error',
      message: body.message ?? 'Algo deu errado. Tente novamente.',
      ...(body.details ? { details: body.details } : {}),
      ...(body.requestId ? { requestId: body.requestId } : {}),
      timestamp: body.timestamp ?? new Date().toISOString(),
      path: body.path ?? path,
    }
  } catch {
    return {
      statusCode: response.status,
      error: 'Error',
      message: 'Algo deu errado. Tente novamente.',
      timestamp: new Date().toISOString(),
      path,
    }
  }
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),

  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),

  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),

  delete: <T>(path: string, options?: Omit<RequestOptions, 'method'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),

  upload: <T>(path: string, formData: FormData, options?: Omit<RequestOptions, 'method' | 'formData'>) =>
    request<T>(path, { ...options, method: 'POST', formData }),
}
